import React from 'react';
import {
  Component,
  Container,
  FilterCombo,
  PuzzleTooltip,
  WithTooltip
} from 'puzzle';
import { STATE, PROPS, DIVIDER, DYNAMIC_OBJECT } from "./Types";
import { QUOTE, SCOPE, VENDOR, QUOTES_RESPONSE, LINE_ITEM } from "../Types";
import * as _ from "lodash";
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { isNull } from "util";
import Big from "big.js";
import classNames from "classnames";
import { Field } from "../../../../UI/Field/Field";

class RateDetails extends Component<PROPS, STATE> {
  tooltip: React.RefObject<PuzzleTooltip> = React.createRef();
  static defaultProps: PROPS = {
    languageId: '',
    quotesData: {
      hasGlobalComments: false,
      vendors: [],
      scopes: [],
      quoteConfiguration: {
        autoQuote: false,
        numberPrecision: 0,
        poEnabled: false,
        poMandatory: false,
        quoteLevel: {
          phaseQuotes: false,
          quoteScope: ''
        },
        roundingMode: ''
      },
      quotes: [],
      quoteSupplierCount: 0,
      quoteLanguageCount: 0,
      quoteBatchCount: 0
    },
    vendorId: 0
  };

  state: STATE = {
    data: {},
    lineItemKeys: [],
    phasesList: [],
    scopes: {},
    selectedLanguageId: this.props.languageId,
    selectedVendorId: this.props.vendorId,
    validVendorsIds: [] // contains vendorsIds that have at lest one quote different than REQUESTED
  };

  /**
   * Finds index of last Leverage item and current line item after it
   * @param array
   * @param item
   */
  addAfterLeverage = (array: LINE_ITEM[], item: LINE_ITEM) => {
    let index;
    index = _.findLastIndex(array, (element: LINE_ITEM) => {
      return element.type === 'LEVERAGE';
    });

    if (index > -1) {
      array.splice(index + 1, 0, item);
    } else {
      array.splice(0, 0, item);
    }
  };

  /**
   * Add to set all id's of all vendors that fulfill the condition
   * @param quote
   * @param validVendors
   */
  addValidVendor = (quote: QUOTE, validVendors: Set<number>) => {
    if (quote.status !== 'REQUESTED') {
      validVendors.add(quote.vendorId);
    }
  };

  /**
   *
   * Ads optional line items tittle or supplier added items title
   * @param item
   * @param dividers
   * @param rows
   * @param phase
   * @param batch
   */
  appendDividerRows = (item: LINE_ITEM, dividers: DIVIDER, rows: JSX.Element[], phase: string, batch: string) => {
    if (item.isOptional && dividers.optionalDivider) {
      dividers.optionalDivider = false;
      rows.push(this.renderDividerRow(phase, batch, true));
    } else if (dividers.supplierAddedItemsDivider && item.supplierAddedItem && !item.isOptional) {
      dividers.supplierAddedItemsDivider = false;
      rows.push(this.renderDividerRow(phase, batch));
    }
  };

  /**
   * Places's item to appropriate within lineItems array
   * order(leverage, default, optional{supplier optional or cost model optional}, supplier added default item)
   *
   * @param array
   * @param item
   */
  addItemToAppropriatePosition = (array: LINE_ITEM[], item: LINE_ITEM) => {
    const { supplierAddedItem, isOptional } = item;
    let index;

    if ((supplierAddedItem && item.type === 'DEFAULT')) {
      array.push(item);
    } else if ((item.type === 'LEVERAGE')) {
      this.addAfterLeverage(array, item);
    } else if (!supplierAddedItem && item.type === 'DEFAULT') {
      index = this.getLastNonSupplierAddedDefaultItem(array);
      if (index > -1) {
        array.splice(index + 1, 0, item)
      } else {
        this.addAfterLeverage(array, item)
      }
    } else if (isOptional) {
      index = _.findLastIndex(array, (element) => {
        return element.isOptional;
      });
      if (index > -1) {
        array.splice(index + 1, 0, item);
      } else {
        index = this.getLastNonSupplierAddedDefaultItem(array);
        if (index > -1) {
          array.splice(index + 1, 0, item);
        } else {
          this.addAfterLeverage(array, item)
        }
      }
    }
  };

  /**
   * Empty cell render
   */
  /**
   * Empty cell render
   */
  emptyCellRender = (cellKey: string): JSX.Element => (
    <div className='tp-grid-cell tp-rate-not-entered tp-col-9' key={cellKey}>
      <span>{this.translate('rateNotEntered')}</span>
    </div>
  );

  /**
   * Renders cell
   * @param value
   * @param key
   */
  cellRender = (value: LINE_ITEM, key: string): JSX.Element => {
    const { quantity, rate, unit, total, isOptional, costModelRate } = value;
    const precision = this.getPrecision();
    const isPercentage = unit === "PERCENTAGE";
    let totalClass = 'tp-grid-cell-item-total';
    let displayTotal: string = isPercentage || isOptional ? new Big(quantity).times(rate).toFixed(precision) : new Big(total || 0).toFixed(precision);
    let cellClass = 'tp-color-light-LM';

    if (!isNull(costModelRate)) {
      if (rate > costModelRate) {
        cellClass = 'tp-rate-up'
      } else if (rate < costModelRate) {
        cellClass = 'tp-rate-down'
      }
    }

    return (
      <div className={'tp-grid-cell tp-col-9 k-hbox'} key={key} role={'gridcell'} data-qa={key}>
        <div className={'tp-grid-cell-rate ' + cellClass}>
          <span className={'tp-grid-cell-item k-justify-content-end'}>{isPercentage ? '' : quantity}</span>
          {this.rateItemRender(isPercentage, value)}
        </div>
        <span
          className={'tp-grid-cell-item-total tp-text-medium k-text-right ' + totalClass}>{displayTotal}</span>
      </div>
    )
  };


  /**
   * Render arrow indicator for rates
   * @param value
   */
  rateArrowRender = (value: LINE_ITEM): JSX.Element => {
    const iconClass = classNames(
      'tp-icon',
      {
        'tp-rate-arrow-up': value.rate > value.costModelRate,
        'tp-rate-arrow-down': value.rate < value.costModelRate
      }
    );
    return (
      <span className={'tp-rate-icon'}>
            <i aria-hidden="true" className={iconClass} />
        </span>
    );
  };

  /**
   * Checks if view type QUOTE_VIEW_TYPE.SUPPLIER
   */
  isSupplierView = (): boolean => !!this.state.selectedVendorId;

  /**
   * Check if quote scope is configured as JOB
   */
  isQuoteJobLvl = (): boolean => this.props.quotesData.quoteConfiguration.quoteLevel.quoteScope === "JOB";

  /**
   * Precision for rounding BIG JS objects
   */
  getPrecision = () => this.props.quotesData.quoteConfiguration.numberPrecision;

  /**
   * Currency getter
   */
  getCurrency = (): string => this.props.quotesData.quotes[0].currency;

  /**
   * Group data by given props (supplier/language and simple or phaseQuotes)
   *
   * @param response
   */
  groupData = (response: QUOTES_RESPONSE) => {
    const { phaseQuotes } = this.props.quotesData.quoteConfiguration.quoteLevel;
    let scopes: DYNAMIC_OBJECT = {};
    let phasesList: Set<string> = new Set();
    let data: DYNAMIC_OBJECT = {};
    let lineItemKeys: any = phaseQuotes ? {} : [];
    let validVendorsIds: Set<number> = new Set();
    response.quotes.forEach(this.extractQuotesData.bind(this, data, scopes, lineItemKeys, response, phaseQuotes, phasesList, validVendorsIds));

    return {
      data,
      lineItemKeys,
      phasesList: [...phasesList],
      scopes,
      validVendorsIds: [...validVendorsIds]
    }
  };

  /**
   * Creates list of languages and phases form scope
   *
   * @param scopes
   * @param phaseQuotes
   * @param phasesList
   * @param quote
   * @param viewIterator
   */
  extractScopesLanguagesQuoteLevel = (scopes: DYNAMIC_OBJECT, phaseQuotes: boolean, phasesList: Set<string>, quote: QUOTE, viewIterator: string) => {
    const { languageName, phaseName } = quote.quoteScope;
    let phases;
    let element = scopes[viewIterator];
    if (element) {
      if (phaseQuotes) {
        phases = element.phases.map((item: any) => item.name);
        if (!phases.includes(phaseName)) {
          element.phases.push({ name: phaseName, phaseTotal: null });
        }
      }
    } else {
      scopes[viewIterator] = {
        ...(this.isSupplierView ? { languageName: languageName } : { vendorName: viewIterator }),
        ...(phaseQuotes && { phases: [{ name: phaseName, phaseTotal: null }] }),
        total: null
      };
    }
    phaseName && phasesList.add(phaseName);
  };

  /**
   * Goes through response.quotes data and sets data to proper format regarding
   * viewType: 'Supplier' or 'Languages" && quoteLvl: 'JOB' or 'LANGUAGE' && phaseQuotes
   *
   * @param data
   * @param scopes
   * @param lineItemKeys
   * @param response
   * @param phaseQuotes
   * @param phasesList
   * @param validVendorsIds
   * @param quote
   */
  extractQuotesData = (data: DYNAMIC_OBJECT, scopes: DYNAMIC_OBJECT, lineItemKeys: any, response: QUOTES_RESPONSE, phaseQuotes: boolean, phasesList: Set<string | any>, validVendorsIds: Set<number>, quote: QUOTE) => {
    const { selectedLanguageId, selectedVendorId } = this.state;
    const { languageId, phaseName, batchName, languageName } = quote.quoteScope;
    const { totalWithoutOptionals, vendorId } = quote;
    const isJobLevel = this.isQuoteJobLvl();
    const supplierView = this.isSupplierView();
    let path: string[];
    let oldTotal;

    let total = new Big(totalWithoutOptionals || 0);
    this.addValidVendor(quote, validVendorsIds);

    if ((supplierView && quote.vendorId === selectedVendorId) || (!supplierView && languageId === selectedLanguageId)) {
      let viewIterator: any = supplierView ? languageId : _.find(response.vendors, (item: VENDOR) => {
        return vendorId === item.vendorId
      })?.vendorName;

      if (isJobLevel) {
        if (!_.includes(Object.keys(scopes), viewIterator)) {
          _.set(scopes, [viewIterator], { total: total, languageName });
        } else {
          oldTotal = _.get(scopes, [viewIterator, 'total']);
          _.set(scopes, [viewIterator, 'total'], oldTotal.plus(total));
        }
        path = batchName && phaseName ? [viewIterator, batchName, phaseName] : [viewIterator, batchName];
        _.set(scopes, path, { jobTotal: total });

      } else {
        this.extractScopesLanguagesQuoteLevel(scopes, phaseQuotes, phasesList, quote, viewIterator);
        let totalPerLanguage = scopes[viewIterator].total;
        if (phaseQuotes) {
          let index = scopes[viewIterator].phases.findIndex((item: any) => item.name === phaseName);
          if (index > -1) {
            scopes[viewIterator].phases[index].phaseTotal = isNull(scopes[viewIterator].phases[index].phaseTotal) ? total : totalPerLanguage.plus(total);
          }
        }
        scopes[viewIterator].total = isNull(totalPerLanguage) ? total : totalPerLanguage.plus(total);
      }

      this.addLineItem(lineItemKeys, quote);
      quote.lineItems.forEach((lineItem: LINE_ITEM) => {
        const { name } = lineItem;
        if (isJobLevel) {
          path = phaseQuotes ? [batchName, phaseName, viewIterator, name] : [batchName, viewIterator, name];
        } else {
          path = phaseQuotes ? [viewIterator, phaseName, name] : [viewIterator, name];
        }
        _.set(data, path, { ...lineItem });
      });
    }
  };


  /**
   * Add's line items to phases or to line item array
   * @param lineItemStore
   * @param quote
   */
  addLineItem = (lineItemStore: any, quote: QUOTE): void => {
    const { quoteScope, lineItems: quoteLineItems } = quote;
    const { phaseName, batchName } = quoteScope;
    let items;
    let iterator: string;

    if (batchName && phaseName) {
      //job phases
      if (!_.includes(Object.keys(lineItemStore), batchName)) {
        lineItemStore[batchName] = {};
      }

      if (!_.includes(Object.keys(lineItemStore[batchName]), phaseName)) {
        lineItemStore[batchName][phaseName] = [];
        [...quoteLineItems].forEach((item) => this.addItemToAppropriatePosition(lineItemStore[batchName][phaseName], { ...item }));
      } else {
        items = _.pullAllBy([...quoteLineItems], lineItemStore[batchName][phaseName], 'name');
        if (items.length) {
          items.forEach((item: LINE_ITEM) => this.addItemToAppropriatePosition(lineItemStore[batchName][phaseName], { ...item }));
        }
      }

    } else if (batchName || phaseName) {
      //job batch or language phases
      iterator = batchName || phaseName || '';
      if (!_.includes(Object.keys(lineItemStore), iterator)) {
        lineItemStore[iterator] = [];
        [...quoteLineItems].forEach((item) => this.addItemToAppropriatePosition(lineItemStore[iterator], { ...item }));
      } else {
        items = _.pullAllBy([...quoteLineItems], lineItemStore[iterator], 'name');
        if (items.length) {
          items.forEach((item: LINE_ITEM) => this.addItemToAppropriatePosition(lineItemStore[iterator], { ...item }));
        }
      }
    } else {
      //language
      items = _.pullAllBy([...quoteLineItems], lineItemStore, 'name');
      if (items.length) {
        items.forEach((item: LINE_ITEM) => this.addItemToAppropriatePosition(lineItemStore, { ...item }));
      }
    }
  };

  /**
   * Get last non supplier added default item
   * @param array
   */
  getLastNonSupplierAddedDefaultItem = (array: LINE_ITEM[]): number => {
    return _.findLastIndex(array, (element) => {
      return element.type === 'DEFAULT' && !element.supplierAddedItem;
    });
  };

  /**
   * Languages quoteLevel render
   */
  languagesViewRender = (): JSX.Element[] => {
    let { data, lineItemKeys, scopes } = this.state;
    let keys = Object.keys(scopes);
    let dividers: DIVIDER = {
      optionalDivider: true,
      supplierAddedItemsDivider: true
    };
    let lineItems: JSX.Element[] = [];

    let rows: JSX.Element[] = [];
    lineItemKeys.forEach((item: LINE_ITEM) => {
      lineItems = [];
      this.appendDividerRows(item, dividers, rows, '', '');

      keys.forEach((key: string) => {
        let value: any = _.get(data, [key, item.name]);
        let cellKey = `${key + '-' + item.name}`;
        if (value) {
          lineItems.push(this.cellRender(value, cellKey));
        } else {
          lineItems.push(this.emptyCellRender(cellKey));
        }
      });
      rows.push(this.rowRender(lineItems, item, ['row', item.name]));
    });

    return rows;
  };

  /**
   * Crates dialog state data
   */
  loadData = () => {
    const {
      data,
      lineItemKeys,
      phasesList,
      scopes,
      validVendorsIds
    } = this.groupData(this.props.quotesData);

    this.setState({
      data,
      lineItemKeys,
      phasesList,
      scopes,
      validVendorsIds
    });
  };

  /**
   * Combobox change handler
   * @param e
   */
  onChange = (e: ComboBoxChangeEvent): void => {
    let { value } = e;
    if (value) {
      value = this.isSupplierView() ? value.vendorId : value.languageId;
      // @ts-ignore
      this.setState({
          [(e.target as any).component.name]: value
        }, this.loadData
      )
    }
  };

  /**
   * Languages with phases and job without phases render
   */
  batchAndPhaseLanguageRender = (): JSX.Element[] => {
    let { data, lineItemKeys, scopes, phasesList } = this.state;
    let dividers: DIVIDER = {
      optionalDivider: true,
      supplierAddedItemsDivider: true
    };
    let keys = Object.keys(scopes);
    let lineItems: JSX.Element[];
    let rows: JSX.Element[] = [];

    const iterable = this.isQuoteJobLvl() ? Object.keys(lineItemKeys) : phasesList;
    iterable.forEach((phaseOrBatch: string) => {
        dividers.optionalDivider = true;
        dividers.supplierAddedItemsDivider = true;

        !!lineItemKeys[phaseOrBatch].length && rows.push(this.tableSectionRender('', phaseOrBatch));
        lineItemKeys[phaseOrBatch].forEach((item: LINE_ITEM) => {
          lineItems = [];
          this.appendDividerRows(item, dividers, rows, phaseOrBatch, '');

          keys.forEach((key: string) => {
            let value: any = this.isQuoteJobLvl() ? _.get(data, [phaseOrBatch, key, item.name]) : _.get(data, [key, phaseOrBatch, item.name]);
            let cellKey = `${phaseOrBatch + '-' + key + '-' + item.name}`;
            if (value) {
              lineItems.push(this.cellRender(value, cellKey));
            } else {
              lineItems.push(this.emptyCellRender(cellKey));
            }
          });
          rows.push(this.rowRender(lineItems, item, ['row', phaseOrBatch, item.name]));
        });
      }
    );

    return rows;
  };


  /**
   * Languages per phase quoteLevel render
   */
  jobPhaseViewRender = (): JSX.Element[] => {
    let { data, lineItemKeys, scopes } = this.state;
    let keys = Object.keys(scopes);
    let lineItems: JSX.Element[];
    let rows: JSX.Element[] = [];
    let path;
    let value;
    let cellKey;
    let dividers: DIVIDER = {
      optionalDivider: true,
      supplierAddedItemsDivider: true
    };

    Object.keys(lineItemKeys).forEach((batch: string) => {
        Object.keys(lineItemKeys[batch]).forEach((phase: string) => {
          dividers.optionalDivider = true;
          dividers.supplierAddedItemsDivider = true;
          !!lineItemKeys[batch][phase].length && rows.push(this.tableSectionRender(batch, phase));

          lineItemKeys[batch][phase].forEach((li: LINE_ITEM) => {
            lineItems = [];
            this.appendDividerRows(li, dividers, rows, phase, batch);
            keys.forEach((language: string) => {
              path = [batch, phase, language, li.name];
              value = _.get(data, path);
              cellKey = path.join('-');
              if (value) {
                lineItems.push(this.cellRender(value, cellKey));
              } else {
                lineItems.push(this.emptyCellRender(cellKey));
              }
            });
            rows.push(this.rowRender(lineItems, li, ['row', batch, phase, li.name]));
          })
        });
      }
    );
    return rows;
  };

  /**
   * Displays batch and phase whit total for that language
   * @param phase
   * @param batch
   */
  tableSectionRender = (batch: string, phase: any): JSX.Element => {
    const { phaseQuotes } = this.props.quotesData.quoteConfiguration.quoteLevel;
    const precision = this.getPrecision();
    const { scopes } = this.state;
    const keys = Object.keys(scopes);
    let totals: JSX.Element[] = [];
    let value: string;
    let path: string[];
    let index;
    const displayValue = !!batch ? batch + ' | ' + phase : phase;

    keys.forEach((key) => {
      value = this.translate('emptyCell');

      if (this.isQuoteJobLvl()) {
        path = phaseQuotes ? [key, batch, phase, 'jobTotal'] : [key, phase, 'jobTotal'];
        value = _.get(scopes, path)?.toFixed(precision) || new Big(0).toFixed(precision);
      } else {
        index = scopes[key].phases.findIndex((item: any) => item.name === phase);
        if (index > -1) {
          value = scopes[key].phases[index].phaseTotal.toFixed(precision);
        }
      }

      totals.push(
        <Container
          justifyContentEnd
          className={'tp-grid-cell tp-col-9'}
          role={'gridcell'}
          key={['separator-row-item', key, batch, phase].join('-')}
        >
          {value}
        </Container>
      );
    });

    return (
      <div className={'tp-grid-row-phase tp-text-semibold tp-font-size-md'}
           key={['separator-row', batch, phase].join('-')}>
        <Container
          justifyContentEnd
          className={'tp-grid-cell tp-col-11 tp-sticky-left'}
          role={'gridcell'}
        >
          <span className={'tp-text-ellipsis'}>{displayValue}</span>
        </Container>
        {totals}
      </div>
    )
  };

  /**
   * Rate render
   * @param isPercentage
   * @param value
   */
  rateItemRender = (isPercentage: boolean, value: LINE_ITEM) => {
    const precision = this.getPrecision();

    let children = <>
      X {new Big(value.rate).toFixed(precision)}
      {!isNull(value.costModelRate) && (this.rateArrowRender(value))}
    </>;

    if (isPercentage) {
      children = <>{new Big(value.rate).toFixed(precision)} %</>;
    }

    return <span className={'tp-grid-cell-item'}>
            {children}
          </span>;
  };

  /**
   * Divider render
   */
  renderDividerRow = (phase: string, batch: string, optionalItemsDivider?: boolean): JSX.Element => {
    const key = optionalItemsDivider ? 'optional-divider' : 'supplier-divider';
    return (
      <div role={'rowgroup'} key={[key, batch, phase].join('-')}>
        <div className={'tp-grid-row tp-grid-row-divider'} role={'row'}>
          <div className={'tp-text-medium tp-grid-cell tp-sticky-left'}
               role={'gridcell'}>
            {optionalItemsDivider ? this.translate('optionalItemsDividerText') : this.translate('supplierItemsDividerText')}
          </div>
        </div>
      </div>
    )
  };

  /**
   * Header row render
   */
  renderHeaderRow = (): JSX.Element => {
    const { scopes } = this.state;
    const precision = this.getPrecision();
    let value;

    let headers = Object.keys(scopes).map((key: string) => {
      const { total, languageName } = scopes[key];
      value = total.toFixed(precision);
      return (
        <div className={'tp-grid-header tp-col-9'} key={'headerCell ' + key} role={'columnheader'}>
          <div className={'tp-grid-header-cell tp-text-semibold'}>{this.isSupplierView() ? languageName : key}</div>
          <div className={'tp-grid-header-cell tp-grid-cell-item-total'}>
            <span>{this.getCurrency()}</span>
            <span className={'tp-text-medium'}>{value}</span>
          </div>
        </div>
      )
    });

    return (
      <div role={'rowgroup'} className={'tp-sticky-top'}>
        <div className={'tp-grid-row tp-grid-row-header'} role={'row'}>
          <div className={'tp-grid-header tp-col-7 tp-sticky-left'} key={'lineItemName'}
               role={'columnheader'}>&nbsp;</div>
          <div className={'tp-grid-header tp-col-4 tp-sticky-left'} key={'unit'} role={'columnheader'}>&nbsp;</div>
          {headers}
        </div>
      </div>
    )
  };

  /**
   * Render different view's depending phaseQuotes(true,false) and quoteScope(JOB,LANGUAGE)
   */
  renderTableBody = () => {
    const { phaseQuotes } = this.props.quotesData.quoteConfiguration.quoteLevel;
    let render;

    if (phaseQuotes) {
      render = this.isQuoteJobLvl() ? this.jobPhaseViewRender() : this.batchAndPhaseLanguageRender();
    } else {
      render = this.isQuoteJobLvl() ? this.batchAndPhaseLanguageRender() : this.languagesViewRender();
    }
    return render;
  };

  /**
   * Renders line item row
   * @param children
   * @param item
   * @param key
   */
  rowRender = (children: JSX.Element[], item: LINE_ITEM, key: string[]): JSX.Element => (
    <div role={'rowgroup'} key={key.join('-')} data-qa={key.join('-')}>
      <div className={'tp-grid-row'} role={'row'}>
        <Container
          justifyContentCenter
          className={'tp-grid-cell tp-text-medium tp-col-7 tp-sticky-left'}
          role={'gridcell'}
        >
          {item.isOptional ?
            <WithTooltip context={this} title={item.localizedName} htmlTag={'span'}>
              <span className={'tp-grid-cell-name'} title={item.localizedName}>
                {item.localizedName}
              </span>
            </WithTooltip> :
            <span className={'tp-grid-cell-name'}>
              {item.localizedName}
            </span>
          }
        </Container>
        <div className={'tp-grid-cell tp-unit-label tp-col-4 tp-sticky-left'}
             role={'gridcell'}
        >
          {this.translate(`units.${item.unit}`)}
        </div>
        {children}
      </div>
    </div>
  );

  renderBody(): JSX.Element {
    const { quotesData } = this.props;
    const {
      selectedVendorId,
      selectedLanguageId,
      validVendorsIds
    } = this.state;
    let comboData: VENDOR[] | SCOPE[] = quotesData.vendors.filter((vendor: VENDOR) => _.includes(validVendorsIds, vendor.vendorId));
    let value: string | number = selectedVendorId;
    let name = 'selectedVendorId';
    let valueField = 'vendorId';
    let textField = 'vendorName';

    if (!this.isSupplierView()) {
      comboData = quotesData.scopes?.reduce((array: SCOPE[], element: SCOPE) => {
        let index = array.findIndex((item: SCOPE) => item.languageId === element.languageId);
        if (index < 0) {
          array.push(element);
        }
        return array
      }, []) || [];
      valueField = 'languageId';
      textField = 'languageName';
      value = selectedLanguageId;
      name = 'selectedLanguageId';
    }

    return (
      <div>
        <Field className={'tp-rate-details-combo-wrap'}>
          <FilterCombo
            label={this.translate('supplierName')}
            className={'tp-rate-details-combo'}
            name={name}
            data={comboData}
            textField={textField}
            onChange={this.onChange}
            translateMethod={this.translate.bind(this)}
            valueField={valueField}
            value={value}
            valueAsSimpleType={true}
          />
        </Field>
        <div className={'tp-rate-details-grid'} role={'grid'} tabIndex={0}>
          {this.renderHeaderRow()}
          <div className={'tp-rate-details-grid-body'}>
            {this.renderTableBody()}
          </div>
        </div>
        <PuzzleTooltip
          anchorElement={'target'}
          ref={(el: any) => this.tooltip = el}
          openDelay={1000}
          position="right"
        />
      </div>
    );
  }
}

export default RateDetails;
